import React, { useLayoutEffect } from 'react';
import { v4 } from 'uuid';
import { Nullable } from 'global';
import { InputField } from 'components/form';
import { Column } from 'components/shared/TableListing/types';
import { Listing, useListing } from 'components/shared/TableListing';
import { CriteriaWithScoreBodyCell } from 'modules/jsonRulesCriterias/scorecardCriteria/ui/cells/CriteriaWithScoreBodyCell';
import {
  allFacts,
  CriteriaHeadCell,
  CustomCriteriaBodyCell,
  CustomCriteriaHeadCell,
  getAvailableConditions,
} from 'modules/jsonRulesCriterias/scorecardCriteria';
import { ActionPanel } from './ActionPanel';
import { BottomActionPanel } from './BottomActionPanel';
import {
  CustomCriteria,
  ScoreCriteriaEntity,
  ScoreCriteriasFormPath,
  ScoreCriteriasTableContext,
  CustomScorecardCriteriaType,
} from '../types';
import { initialScoreCriteriaColumns, initialScoreCriteriaTableData, newScoreCriteria } from '../constants';

interface Props {
  initialColumnTypes: Nullable<string[]>;
  initialData: ScoreCriteriaEntity[];
  onListingChange: (value: ScoreCriteriaEntity[]) => void;
  scoreCustomCriteria: ScoreCriteriaEntity['customCriteria'];
}

export const CriteriaBuilder = ({ initialData, onListingChange, initialColumnTypes, scoreCustomCriteria }: Props) => {
  const getCriteriaColumnConfig = (type: string) => {
    return {
      header: 'Criteria',
      renderKey: type,
      columnType: type,
      renderHeadCell: ({ existingColumnsTypes, column, columnIndex }) => {
        const onChange = (nextValue: string) => {
          unregisterBodyFieldsByColumnId(column.columnType!, 'criteria');
          handleUpdateColumnType(columnIndex, nextValue);
        };

        const onRemove = () => {
          unregisterBodyFieldsByColumnId(column.columnType!, 'criteria');
          handleRemoveColumn(columnIndex);
        };

        return (
          <CriteriaHeadCell
            onChange={onChange}
            onRemove={onRemove}
            listOfFacts={allFacts}
            currentValue={column.columnType}
            addedCriteriaTypes={existingColumnsTypes}
          />
        );
      },
      renderCell: ({ context: { form }, column, rowIndex }) => {
        const name = `bodyFields.${rowIndex}.criteria.${column.columnType}` as ScoreCriteriasFormPath;

        return <CriteriaWithScoreBodyCell control={form.control} ruleType={column.columnType} name={name} />;
      },
    } satisfies Column<ScoreCriteriaEntity, ScoreCriteriasTableContext>;
  };

  const syncCustomCriteriaColumnWithBody = (
    columnId: string,
    valueKey: keyof CustomCriteria | string,
    nextType: CustomScorecardCriteriaType | string | number | null
  ) => {
    form.getValues('bodyFields').forEach((_, rowIndex) => {
      const valuePath = `bodyFields.${rowIndex}.customCriteria.${columnId}.${valueKey}` as ScoreCriteriasFormPath;

      form.setValue(valuePath, nextType);
    });
  };

  const getCustomCriteriaColumnConfig = (column: Nullable<Partial<CustomCriteria>>, syncWithBody = false) => {
    const customCriteriaColumn: CustomCriteria = {
      name: column?.name ?? '',
      value: column?.value ?? '',
      score: column?.score ?? '0',
      internalId: column?.internalId ?? v4(),
      type: column?.type ?? CustomScorecardCriteriaType.Text,
    };

    if (syncWithBody) {
      Object.entries(customCriteriaColumn).forEach(([key, value]) => {
        syncCustomCriteriaColumnWithBody(customCriteriaColumn.internalId, key, value);
      });
    }

    return {
      header: 'Criteria',
      renderKey: 'customCriteria',
      columnMeta: {
        name: customCriteriaColumn.name,
        id: customCriteriaColumn.internalId,
        customType: customCriteriaColumn.type,
      },
      renderHeadCell: ({ column, columnIndex }) => {
        const onColumnTypeChange = (nextValue: CustomScorecardCriteriaType) => {
          syncCustomCriteriaColumnWithBody(customCriteriaColumn.internalId, 'type', nextValue);
          handleUpdateColumnMeta(columnIndex, 'customType', nextValue);
        };

        const onColumnNameChange = (nextValue: string) => {
          syncCustomCriteriaColumnWithBody(customCriteriaColumn.internalId, 'name', nextValue);
          handleUpdateColumnMeta(columnIndex, 'name', nextValue);
        };

        const onColumnRemove = () => {
          unregisterBodyFieldsByColumnId(customCriteriaColumn.internalId, 'customCriteria');
          handleRemoveColumn(columnIndex);
        };

        return (
          <CustomCriteriaHeadCell
            actions={{
              onRemove: onColumnRemove,
              onTypeChange: onColumnTypeChange,
              onNameChange: onColumnNameChange,
            }}
            values={{
              name: column.columnMeta?.name ?? '',
              type: column.columnMeta?.customType ?? CustomScorecardCriteriaType.Text,
            }}
          />
        );
      },
      renderCell: ({ context: { form }, column, rowIndex }) => {
        if (!column.columnMeta?.customType || column.columnMeta?.id === undefined) {
          throw new Error('No column meta provided');
        }

        const { customType, id } = column.columnMeta;

        const scorePath = `bodyFields.${rowIndex}.customCriteria.${id}.score` as ScoreCriteriasFormPath;
        const valuePath = `bodyFields.${rowIndex}.customCriteria.${id}.value` as ScoreCriteriasFormPath;

        return (
          <CustomCriteriaBodyCell
            name={valuePath}
            type={customType}
            control={form.control}
            rightSlot={
              <InputField
                label="Score"
                name={scorePath}
                control={form.control}
                inputProps={{ type: 'number', variant: 'outlined', placeholder: 'Enter value...' }}
              />
            }
          />
        );
      },
    } satisfies Column<ScoreCriteriaEntity, ScoreCriteriasTableContext, string, CustomScorecardCriteriaType>;
  };

  const {
    form,
    columns,
    tableData,
    handleAddRow,
    handleAddColumn,
    handleRemoveRow,
    handleCopyLastRow,
    handleRemoveColumn,
    handleUpdateColumnType,
    handleUpdateColumnMeta,
    unregisterBodyFieldsByColumnId,
  } = useListing<ScoreCriteriaEntity, ScoreCriteriasTableContext, string, CustomScorecardCriteriaType>(
    {
      headerColumns: initialScoreCriteriaColumns,
      bodyFields: initialData || initialScoreCriteriaTableData,
    },
    { onSubscribe: onListingChange, shouldUnregister: false }
  );

  const onAddCriteria = () => {
    const added = columns.map(({ columnType }) => columnType);
    const availableCriteriaType = getAvailableConditions(added as string[]);

    if (!availableCriteriaType) return;

    const criteriaColumnConfig = getCriteriaColumnConfig(availableCriteriaType);
    handleAddColumn(criteriaColumnConfig);
  };

  const onAddCustomCondition = (name: string = '') => {
    const customCriteriaColumnConfig = getCustomCriteriaColumnConfig(
      { name, type: CustomScorecardCriteriaType.Text },
      true
    );

    handleAddColumn(customCriteriaColumnConfig);
  };

  const handleAddCriteriaRow = () => {
    handleAddRow(newScoreCriteria);

    const rows = form.getValues('bodyFields');

    const nextRowIndex = Math.max(rows.length - 1, 0);

    const prevRowIndex = nextRowIndex - 1;
    const prevRow = prevRowIndex >= 0 && rows[prevRowIndex] ? (rows[prevRowIndex].customCriteria ?? {}) : {};

    const newRowWithoutValues = Object.entries(prevRow).reduce<ScoreCriteriaEntity['customCriteria']>(
      (acc, [id, entity]) => {
        if (id && entity && acc) acc[id] = { ...entity, value: '', score: '0' };
        return acc;
      },
      {}
    );

    form.setValue(`bodyFields.${nextRowIndex}.customCriteria`, newRowWithoutValues);
  };

  useLayoutEffect(() => {
    if (initialColumnTypes) {
      initialColumnTypes.forEach(type => handleAddColumn(getCriteriaColumnConfig(type)));
      Object.values(scoreCustomCriteria ?? {}).forEach(customCriteria =>
        handleAddColumn(getCustomCriteriaColumnConfig(customCriteria))
      );
    }
  }, []);

  return (
    <Listing<ScoreCriteriaEntity, ScoreCriteriasTableContext>
      columns={columns}
      tableData={tableData}
      injectContext={{ form, handleRemoveRow }}
      actionPanelTop={() => <ActionPanel onAddCondition={onAddCriteria} onAddCustomCondition={onAddCustomCondition} />}
      actionPanelBottom={() => <BottomActionPanel onCopyLast={handleCopyLastRow} onAddRow={handleAddCriteriaRow} />}
    />
  );
};
